/**
 * Editor display metrics. Timecode formatting/parsing and the CPS calculation
 * mirror the canonical model and the QC `max-cps` rule so the numbers shown in
 * the cue table match what the engine reports.
 */
import { cueDurationMs, frameDurationMs, joinMs, lineToText, splitMs, type Cue } from '../../core';
import { BIDI_CONTROL_RANGES } from '../../core/rtl';

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

/** Format integer milliseconds as `HH:MM:SS,mmm` (SRT style; always LTR). */
export function formatTimecode(ms: number): string {
  const { hours, minutes, seconds, millis } = splitMs(Math.max(0, Math.round(ms)));
  return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)},${pad(millis, 3)}`;
}

/** Parse `HH:MM:SS,mmm` / `HH:MM:SS.mmm` to milliseconds; null if malformed. */
export function parseTimecode(input: string): number | null {
  const m = /^(\d{1,3}):([0-5]?\d):([0-5]?\d)[,.](\d{1,3})$/.exec(input.trim());
  if (!m) return null;
  return joinMs({
    hours: Number(m[1]),
    minutes: Number(m[2]),
    seconds: Number(m[3]),
    millis: Number(m[4]!.padEnd(3, '0')),
  });
}

/** Cue duration in milliseconds (delegates to the model). */
export function durationMs(cue: Cue): number {
  return cueDurationMs(cue);
}

function inRanges(code: number, ranges: [number, number][]): boolean {
  for (const [lo, hi] of ranges) {
    if (code >= lo && code <= hi) return true;
  }
  return false;
}

/**
 * Characters per second, matching the engine's `max-cps` behaviour: line
 * breaks excluded, counted by code point, with optional inclusive
 * `excludeRanges` (same shape as `MaxCpsConfig.excludeRanges` — e.g. Arabic
 * harakat in netflix-arabic) skipped. Returns 0 for non-positive durations so
 * the table shows a stable value rather than Infinity.
 */
export function cueCps(cue: Cue, excludeRanges: [number, number][] = []): number {
  const seconds = cueDurationMs(cue) / 1000;
  if (seconds <= 0) return 0;
  // Always exclude the invisible bidi control marks (on top of the configured
  // ranges) so the displayed CPS matches the engine after RTL fixes.
  const ranges = [...BIDI_CONTROL_RANGES, ...excludeRanges];
  let chars = 0;
  for (const line of cue.lines) {
    for (const ch of lineToText(line)) {
      if (inRanges(ch.codePointAt(0)!, ranges)) continue;
      chars++;
    }
  }
  return chars / seconds;
}

/** A metric cell's standing against a QC threshold; null = comfortably within. */
export type ThresholdState = 'error' | 'warning' | null;

/** How close a value sits to a limit before it warns: within 10% (cue-forge-n3e.4). */
const NEAR_FRACTION = 0.1;

/**
 * Classify a CPS value against the active preset's `max-cps` so the cue table
 * can tint cells (amber near the limit, red over). Returns null when there is no
 * configured max, so the table degrades gracefully without QC.
 */
export function cpsThreshold(cps: number, maxCps: number | undefined): ThresholdState {
  if (maxCps === undefined) return null;
  if (cps > maxCps) return 'error';
  if (cps >= maxCps * (1 - NEAR_FRACTION)) return 'warning';
  return null;
}

/**
 * Classify a cue duration against the preset's `min-duration` / `max-duration`:
 * red when shorter than the min or longer than the max, amber within 10% of
 * either bound. Either bound may be absent.
 */
export function durationThreshold(
  ms: number,
  minMs: number | undefined,
  maxMs: number | undefined,
): ThresholdState {
  if (minMs !== undefined && ms < minMs) return 'error';
  if (maxMs !== undefined && ms > maxMs) return 'error';
  if (minMs !== undefined && ms <= minMs * (1 + NEAR_FRACTION)) return 'warning';
  if (maxMs !== undefined && ms >= maxMs * (1 - NEAR_FRACTION)) return 'warning';
  return null;
}

/**
 * A specific tooltip for an over-threshold duration cell, naming the bound and
 * its value (e.g. "Too short (minimum 0.70s)"), mirroring the CPS cell's title.
 * Returns undefined when the duration is comfortably within bounds. Shares the
 * thresholds with {@link durationThreshold} so the two never disagree.
 */
export function durationTitle(
  ms: number,
  minMs: number | undefined,
  maxMs: number | undefined,
): string | undefined {
  const secs = (v: number) => `${(v / 1000).toFixed(2)}s`;
  if (minMs !== undefined && ms < minMs) return `Too short (minimum ${secs(minMs)})`;
  if (maxMs !== undefined && ms > maxMs) return `Too long (maximum ${secs(maxMs)})`;
  if (minMs !== undefined && ms <= minMs * (1 + NEAR_FRACTION))
    return `Near the minimum duration (${secs(minMs)})`;
  if (maxMs !== undefined && ms >= maxMs * (1 - NEAR_FRACTION))
    return `Near the maximum duration (${secs(maxMs)})`;
  return undefined;
}

/**
 * Label for a drift rate (cue-forge-d9b.1). Drift is inherently a *rate*, so the
 * primary unit stays the conventional signed `ms/min`; when the document declares
 * a frame rate a `fr/min` hint is appended (one decimal — drift is often a
 * fraction of a frame per minute) so broadcast operators can judge it in frames.
 */
export function driftRateLabel(msPerMinute: number, fps?: number): string {
  const ms = Math.round(msPerMinute);
  const base = `${ms >= 0 ? '+' : ''}${ms} ms/min`;
  if (fps === undefined) return base;
  const framesPerMinute = msPerMinute / frameDurationMs(fps);
  return `${base} (≈ ${framesPerMinute.toFixed(1)} fr/min)`;
}
